import {
  loadModelsCatalog,
  loadModelsCatalogWithWarnings,
  MODELS_CATALOG_REMOTE_URL,
  type RegistrySourceMode,
} from "@x12i/ai-profiles";
import type { AiModelRecord } from "../models/types.js";
import {
  DEFAULT_DIRECT_CATALOG_URL,
  DEFAULT_MODELS_CATALOG_URL,
  DEFAULT_OPENROUTER_CATALOG_URL,
} from "./catalogSources.js";
import { modelsFromAiProfilesCatalog } from "./normalizeAiProfilesCatalog.js";

export {
  DEFAULT_DIRECT_CATALOG_URL,
  DEFAULT_MODELS_CATALOG_URL,
  DEFAULT_OPENROUTER_CATALOG_URL,
};

export type LoadCatalogOptions = {
  /**
   * Catalog source mode for `@x12i/ai-profiles` (`auto` | `remote` | `bundled`).
   * When `bundledOnly` is true, forced to `bundled`.
   */
  source?: RegistrySourceMode;
  /** When true, skip remote and use ai-profiles bundled catalog only. */
  bundledOnly?: boolean;
  /** @deprecated Ignored — catalog SoT is `@x12i/ai-profiles`. */
  directCatalogUrl?: string;
  /** @deprecated Ignored — catalog SoT is `@x12i/ai-profiles`. */
  openRouterCatalogUrl?: string;
  /** @deprecated Ignored — fetch timeout is owned by ai-profiles. */
  fetchTimeoutMs?: number;
};

export type LoadedCatalogs = {
  direct: Map<string, AiModelRecord>;
  openrouter: Map<string, AiModelRecord>;
  meta: {
    directSource: "remote" | "bundled";
    openRouterSource: "remote" | "bundled";
    directUrl: string;
    openRouterUrl: string;
    directCount: number;
    openRouterCount: number;
  };
};

function resolveSource(options: LoadCatalogOptions): RegistrySourceMode {
  if (options.bundledOnly) return "bundled";
  return options.source ?? "auto";
}

/** Load unified models catalog from `@x12i/ai-profiles` (direct + OpenRouter slices). */
export async function loadCatalogSources(
  options: LoadCatalogOptions = {},
): Promise<LoadedCatalogs> {
  const source = resolveSource(options);
  const { catalog, source: loadedFrom } = await loadModelsCatalogWithWarnings({
    source,
    ...(source !== "auto" ? { refresh: true as const } : {}),
  });

  const { direct, openrouter } = modelsFromAiProfilesCatalog(catalog);
  const url = MODELS_CATALOG_REMOTE_URL;

  return {
    direct,
    openrouter,
    meta: {
      directSource: loadedFrom,
      openRouterSource: loadedFrom,
      directUrl: url,
      openRouterUrl: url,
      directCount: direct.size,
      openRouterCount: openrouter.size,
    },
  };
}

/** Load ai-profiles bundled catalog only (CLI / tests). */
export async function readBundledCatalogFiles(): Promise<LoadedCatalogs> {
  return loadCatalogSources({ bundledOnly: true });
}

/** Convenience: load catalog JSON via ai-profiles without mapping to AiModelRecord. */
export async function loadAiProfilesModelsCatalog(
  options: LoadCatalogOptions = {},
) {
  return loadModelsCatalog({
    source: resolveSource(options),
  });
}
